import { useQuery } from '@tanstack/react-query';
import {
  audit,
  credentials,
  dashboard,
  eligibility,
  forms,
  incidents,
  isForbidden,
  supervision,
} from '@cwos/api-client';
import type { audit as AuditNS } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet, TableCard, type Column } from '@cwos/ui-admin';
import { formatCount } from '@cwos/ui-shared';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsHeader } from '../settings/common.js';
import { controlPresentation, formatEventTime, looksLikePayload } from './auditLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * The Compliance Center — "can I prove it?".
 *
 * Every statement on this screen comes from a live query against the
 * organization's own data. There is no static control list and no aspirational
 * copy.
 *
 * WHAT THIS SCREEN WILL NEVER SHOW: a certification badge. Phibian holds no
 * SOC 2, HITRUST or ISO 27001 attestation, and a green tick next to a control
 * would be read as one. Controls are reported as "In place" / "Not set up" —
 * observations about configuration — and the notice explaining the difference is
 * served by the API, so it cannot be edited away here.
 */
export function ComplianceCenter() {
  const permissions = usePermissions();
  const { has } = permissions;
  const navigate = useNav();
  /**
   * FOUR ANSWERS, NOT TWO — the same change and the same reason as `AuditLog`,
   * which shares this key and this route gate.
   *
   * THIS BRANCH IS LATENT AND IS FIXED ANYWAY. `audit:read` is also the key the
   * ROUTE is gated on, so `RequirePermission` answers the unresolved set with a
   * skeleton and the unreadable set with its own error panel, and neither state
   * reaches here today. It is dead because two keys happen to be identical, not
   * because the site is right, and the day the route key and the screen key
   * diverge it wakes up saying the wrong thing. Recording it as latent is the
   * reason to fix it cheaply, not the reason to leave it.
   */
  const readGate = authorityGate(permissions, 'audit:read');
  const canRead = gateOpen(readGate);
  const canReadPeople = has('user:read');

  const overview = useQuery({
    queryKey: ['compliance', 'overview'],
    queryFn: () => audit.complianceOverview({ days: 90 }),
    enabled: canRead,
  });

  const access = useQuery({
    queryKey: ['compliance', 'access'],
    queryFn: () => audit.accessEvidence(),
    enabled: canRead && canReadPeople,
  });

  if (!gateOpen(readGate)) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <SettingsHeader title="Compliance" />
        <GateNotice
          gate={readGate}
          what="see compliance evidence"
          onRetry={() => permissions.refetch()}
          heading="You don’t have access to compliance evidence"
          detail="This area needs the audit permission, which is held by organization administrators."
        />
      </div>
    );
  }

  if (overview.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 1080 }}>
        <SkeletonSet surface="table" rows={8} />
      </div>
    );
  }

  // A 403 arriving after `canRead` passed means the permission was taken away
  // mid-session. That is an ANSWER, and `ErrorPanel` answered it with a "Try
  // again" button that will 403 forever.
  if (overview.isError && isForbidden(overview.error)) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <Refused
          heading="Compliance evidence is not shown to you"
          message="Your account no longer has access to the audit record. Nothing has been left out of it — you are not being shown it."
        />
      </div>
    );
  }
  if (
    overview.isError ||
    !looksLikePayload<AuditNS.ComplianceOverview>(overview.data, 'auditActivity')
  ) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <LoadFailure
          error={overview.error}
          message="We couldn’t load compliance evidence just now. Nothing here should be read as a count of zero."
          onRetry={() => void overview.refetch()}
        />
      </div>
    );
  }

  const data = overview.data;
  const activity = data.auditActivity;

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Compliance"
        blurb={`What ${data.organizationName} has configured, and the evidence Phibian has recorded.`}
      />

      {/* STATUS FIRST, THEN DRILL DOWN (Phase I — owner directive): the
          standing strip answers "where do we stand" before the evidence
          sections below explain it. Each tile is a DOOR into its real
          surface, drawn only for a principal whose key opens that read, and
          each says whose population its numbers count. */}
      <ComplianceStanding />

      {/* Served by the API, deliberately: the statement that Phibian claims no
          certification belongs to the domain that owns the meaning, not to a
          component someone could reword. */}
      <SettingsCard>
        <h2 className="rn-card-title">What this page is</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {data.attestationNotice}
        </p>
      </SettingsCard>

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Recorded activity</h2>
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => navigate('/compliance/audit')}
          >
            Open the audit log
          </button>
        </div>
        <div className="cmp-stats">
          <Stat
            label="Events recorded"
            value={formatCount(activity.totalEvents)}
            hint="Last 90 days"
          />
          <Stat
            label="Views of sensitive data"
            value={formatCount(activity.privacyReads)}
            hint="Location history, client names"
          />
          <Stat
            label="Access & security changes"
            value={formatCount(activity.securityChanges)}
            hint="Roles, permissions, policies"
          />
          <Stat
            label="Earliest record"
            value={activity.earliest ? formatEventTime(activity.earliest).split(' · ')[0]! : '—'}
            hint={activity.earliest ? 'Nothing before this is recorded' : 'No activity yet'}
          />
        </div>
        {activity.byCategory.length > 0 ? (
          <ul className="cmp-catbars">
            {activity.byCategory.map((c) => (
              <li key={c.key}>
                <button
                  type="button"
                  className="cmp-catbar"
                  onClick={() => navigate(`/compliance/audit?category=${c.key}`)}
                >
                  <span>{c.label}</span>
                  <span className="cmp-catbar-count">{formatCount(c.eventCount)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
            No activity has been recorded in the last 90 days.
          </p>
        )}
      </SettingsCard>

      {data.sections.map((section) => (
        <SettingsCard key={section.key}>
          <h2 className="rn-card-title">{section.title}</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            {section.description}
          </p>
          <ul className="cmp-controls">
            {section.controls.map((control) => (
              <ControlRow key={control.key} control={control} onManage={navigate} />
            ))}
          </ul>
        </SettingsCard>
      ))}

      {canReadPeople ? (
        <AccessEvidenceCard query={access} />
      ) : (
        <SettingsCard>
          <h2 className="rn-card-title">Access review</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Reviewing who has access needs permission to view people in the organization, which you
            don’t hold. The controls above still describe the shape of access without naming anyone.
          </p>
        </SettingsCard>
      )}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="cmp-stat">
      <span className="cmp-stat-value">{value}</span>
      <span className="cmp-stat-label">{label}</span>
      {hint ? <span className="rn-muted cmp-stat-hint">{hint}</span> : null}
    </div>
  );
}

function ControlRow({
  control,
  onManage,
}: {
  control: AuditNS.ComplianceControl;
  onManage: (href: string) => void;
}) {
  const presentation = controlPresentation(control.state);
  return (
    <li className="cmp-control">
      <div className="cmp-control-head">
        <span className="cmp-control-title">{control.title}</span>
        {/* "In place" / "Not set up" — an observation, never a pass/fail tick. */}
        <Pill variant={presentation.variant} dot>
          {presentation.label}
        </Pill>
      </div>
      <p className="rn-muted cmp-control-desc">{control.description}</p>
      <p className="cmp-control-detail">{control.detail}</p>
      {control.manageHref ? (
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => onManage(control.manageHref!)}
        >
          Manage this
        </button>
      ) : null}
    </li>
  );
}

/**
 * Access evidence: members, their roles, and — the part People does not give in
 * one place — direct permission grants and holders of the location-history
 * permissions. That is what an access review actually needs to look at.
 */
function AccessEvidenceCard({
  query,
}: {
  query: ReturnType<typeof useQuery<AuditNS.AccessEvidence>>;
}) {
  if (query.isLoading) {
    return (
      <SettingsCard>
        <h2 className="rn-card-title">Access review</h2>
        <SkeletonSet surface="table" rows={4} />
      </SettingsCard>
    );
  }
  if (query.isError && isForbidden(query.error)) {
    return (
      <SettingsCard>
        <h2 className="rn-card-title">Access review</h2>
        <Refused
          heading="The access review is not shown to you"
          message="Reviewing who has access needs permission to view people in the organization. This is not an organization with no members."
        />
      </SettingsCard>
    );
  }
  if (query.isError || !looksLikePayload<AuditNS.AccessEvidence>(query.data, 'members')) {
    return (
      <SettingsCard>
        <h2 className="rn-card-title">Access review</h2>
        <LoadFailure
          error={query.error}
          message="We couldn’t load the access review just now. Read the figures above as unknown, not as zero."
          onRetry={() => void query.refetch()}
        />
      </SettingsCard>
    );
  }

  const data = query.data;
  const columns: Column<AuditNS.AccessEvidenceMember>[] = [
    { key: 'name', header: 'Person', render: (m) => m.name || m.email },
    { key: 'status', header: 'Account', render: (m) => m.status },
    { key: 'roles', header: 'Roles', render: (m) => (m.roles.length ? m.roles.join(', ') : '—') },
    {
      key: 'direct',
      header: 'Direct permissions',
      render: (m) => (m.directPermissions.length ? m.directPermissions.join(', ') : '—'),
    },
    {
      key: 'last',
      header: 'Last signed in',
      render: (m) =>
        m.lastSignedInAt ? formatEventTime(m.lastSignedInAt).split(' · ')[0]! : 'Never',
    },
  ];

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Access review</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        Everyone who can sign in, what they can do, and any permission granted to them individually
        rather than through a role.
      </p>

      <div className="cmp-stats">
        <Stat label="Members" value={formatCount(data.totalMembers)} />
        <Stat label="Active" value={String(data.activeMembers)} />
        <Stat
          label="With direct grants"
          value={String(data.membersWithDirectGrants)}
          hint="Permissions held outside a role"
        />
        <Stat
          label="Can view location history"
          value={String(data.locationHistoryViewers.length)}
          hint="Granted to nobody by default"
        />
      </div>

      {data.locationHistoryViewers.length > 0 ? (
        <div className="cmp-callout">
          <p className="cmp-control-title">Who can view raw location history</p>
          <p className="rn-muted" style={{ fontSize: 13 }}>
            These permissions are granted to no role by default, and every viewing is recorded in
            the audit log.
          </p>
          <ul className="cmp-inline-list">
            {data.locationHistoryViewers.map((v) => (
              <li key={`${v.userId}:${v.permission}`}>
                {v.name} <span className="rn-muted">· {v.permission}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div style={{ marginTop: 'var(--space-12)' }}>
        {/* `TableCard` has no empty state of its own — zero rows renders a
            header over an empty `<tbody>`, which reads as a rendering bug
            rather than an answer. The payload is already guarded above, so
            zero here really does mean zero. */}
        {data.members.length === 0 ? (
          <p className="rn-muted" role="status">
            The access review returned no members. Every organization has at least one, so this is
            worth reporting rather than acting on.
          </p>
        ) : (
          <TableCard
            columns={columns}
            rows={data.members}
            rowKey={(m) => m.userId}
            caption="Members of this organization"
          />
        )}
      </div>
    </SettingsCard>
  );
}

/**
 * The compliance standing strip: open documentation, supervision standing,
 * credential standing — three existing reads, three doors. A tile whose key
 * this principal does not hold is ABSENT (the rail's rule); a held key whose
 * read fails says so rather than showing a reassuring zero; a caseload
 * answer names itself (the payloads' own scope fields, never re-derived).
 *
 * THREE MORE DOORS, AND WHY THEY CARRY NO FIGURE.
 *
 * Eligibility, Incidents and Forms join the strip as destinations. None of them
 * gets a number, and that is a decision rather than an omission:
 *
 *   ELIGIBILITY — a count of holds is honest (`GET /eligibility/holds` sends
 *     its own `scope`), but it is one more request on a screen that already
 *     makes three, for a figure that is almost always zero. The list itself is
 *     one click away and states its own frame.
 *   INCIDENTS — `GET /api/incidents` sends NO total and NO scope, so any figure
 *     here would be a claim about the reader's reach dressed as a claim about
 *     the organization. That is the exact failure this file's own
 *     `noSupervisor: 0` comment is about, and the honest tile is one with no
 *     number on it.
 *   FORMS — `GET /api/forms` carries no versions, so "3 awaiting review" cannot
 *     be computed without a request per form.
 *
 * A door with no figure is worth having: the capability was unreachable
 * entirely, and a tile that says "open it" is better than a number that is
 * wrong about who it counts.
 */
function ComplianceStanding() {
  const navigate = useNav();
  const { has } = usePermissions();
  const canDocs = has('documentation:read');
  const canSup = has('supervision:read');
  const canCred = has('credential:read');
  const canEligibility = has(eligibility.ELIGIBILITY_READ);
  const canIncidents = has(incidents.INCIDENTS_READ);
  const canForms = has(forms.FORMS_READ);

  const docs = useQuery({
    queryKey: ['dashboard', 'documentation'],
    queryFn: () => dashboard.documentation(),
    enabled: canDocs,
  });
  const sup = useQuery({
    queryKey: ['supervision', 'overview'],
    queryFn: () => supervision.overview(),
    enabled: canSup,
  });
  const cred = useQuery({
    queryKey: ['credentials', 'overview'],
    queryFn: () => credentials.overview(),
    enabled: canCred,
  });

  if (!canDocs && !canSup && !canCred && !canEligibility && !canIncidents && !canForms) {
    return null;
  }

  return (
    <div className="tc-kpis">
      {canDocs ? (
        <button
          type="button"
          className="db-kpi tc-kpi-btn"
          onClick={() => navigate('/documentation')}
        >
          <span className="db-kpi-label">Documentation</span>
          <span className="db-kpi-value">
            {docs.data
              ? docs.data.scope.population === 'none'
                ? '—'
                : docs.data.totalOpen
              : docs.isError
                ? '—'
                : '…'}
          </span>
          <span className="db-kpi-foot">
            <span className="rn-muted">
              {docs.data
                ? docs.data.scope.population === 'none'
                  ? 'Counts nobody you can see'
                  : docs.data.scope.population === 'subset'
                    ? 'open notes · not the whole organization'
                    : 'open notes'
                : docs.isError
                  ? 'Couldn’t be read — not a zero'
                  : 'Reading…'}
            </span>
          </span>
        </button>
      ) : null}
      {canSup ? (
        <button
          type="button"
          className="db-kpi tc-kpi-btn"
          onClick={() => navigate('/workforce/supervision')}
        >
          <span className="db-kpi-label">Supervision</span>
          <span className="db-kpi-value">
            {sup.data ? sup.data.overdue + sup.data.due : sup.isError ? '—' : '…'}
          </span>
          <span className="db-kpi-foot">
            <span className="rn-muted">
              {sup.data
                ? `due or overdue${sup.data.scope === 'caseload' ? ' · your caseload' : ''}`
                : sup.isError
                  ? 'Couldn’t be read — not a zero'
                  : 'Reading…'}
            </span>
          </span>
        </button>
      ) : null}
      {canCred ? (
        <button
          type="button"
          className="db-kpi tc-kpi-btn"
          onClick={() => navigate('/workforce/credentials')}
        >
          <span className="db-kpi-label">Credentials</span>
          <span className="db-kpi-value">
            {cred.data ? cred.data.expired + cred.data.expiringSoon : cred.isError ? '—' : '…'}
          </span>
          <span className="db-kpi-foot">
            <span className="rn-muted">
              {cred.data
                ? `expired or expiring soon${cred.data.scope === 'caseload' ? ' · your caseload' : ''}`
                : cred.isError
                  ? 'Couldn’t be read — not a zero'
                  : 'Reading…'}
            </span>
          </span>
        </button>
      ) : null}
      {/* THE THREE DOORS. No figure — see this component's header for why each
          of the three would be a claim the endpoint cannot support. */}
      {canEligibility ? (
        <button
          type="button"
          className="db-kpi tc-kpi-btn"
          onClick={() => navigate('/workforce/eligibility')}
        >
          <span className="db-kpi-label">Eligibility</span>
          <span className="db-kpi-value">·</span>
          <span className="db-kpi-foot">
            <span className="rn-muted">who is held back from service work</span>
          </span>
        </button>
      ) : null}
      {canIncidents ? (
        <button
          type="button"
          className="db-kpi tc-kpi-btn"
          onClick={() => navigate('/compliance/incidents')}
        >
          <span className="db-kpi-label">Incidents</span>
          <span className="db-kpi-value">·</span>
          <span className="db-kpi-foot">
            <span className="rn-muted">what happened · who was told</span>
          </span>
        </button>
      ) : null}
      {canForms ? (
        <button
          type="button"
          className="db-kpi tc-kpi-btn"
          onClick={() => navigate('/compliance/forms')}
        >
          <span className="db-kpi-label">Forms</span>
          <span className="db-kpi-value">·</span>
          <span className="db-kpi-foot">
            <span className="rn-muted">instruments · approvals · publication</span>
          </span>
        </button>
      ) : null}
    </div>
  );
}
